import { useCallback, useEffect, useState } from 'react';

import { useAppStore } from '../../../store/useAppStore';
import { initAuth } from '../../auth';
import type { UserProfile } from '../../auth/types';
import {
    handleSignedOutSessionState,
    hasSignedOutSessionStateToPreserve,
    SIGNED_OUT_AUTH_CLEAR_DELAY_MS,
    shouldIgnoreSignedOutSessionForSeededLocalAuth,
} from '../auth-session-reset';

type SignedOutPreservation = {
    projectId: string | null;
    userId: string | null;
};

type PendingSignedOutCleanup = SignedOutPreservation & {
    timer: ReturnType<typeof setTimeout>;
};

type AuthUserSetter = (nextUser: UserProfile | null) => void;
type AuthSessionActions = {
    clearAuthUser: () => void;
    clearCloudAuthority: () => void;
    clearCloudSession: () => void;
    setAuthUser: AuthUserSetter;
    setHasResolvedAuth: (nextValue: boolean) => void;
    setUser: AuthUserSetter;
};

const PRESERVE_SEEDED_LOCAL_AUTH = (
    import.meta.env.VITE_VISUAL_LOCAL_DEMO === 'true'
    || import.meta.env.VITE_E2E === 'true'
);

let pendingSignedOutCleanup: PendingSignedOutCleanup | null = null;
let lastSignedOutPreservation: SignedOutPreservation | null = null;
const activeAuthUserSetters = new Set<AuthUserSetter>();

function notifyActiveAuthUserSetters(nextUser: UserProfile | null): void {
    for (const setActiveUser of activeAuthUserSetters) {
        setActiveUser(nextUser);
    }
}

function getSignedOutPreservation(state = useAppStore.getState()): SignedOutPreservation | null {
    if (!hasSignedOutSessionStateToPreserve(state)) {
        return null;
    }

    return {
        projectId: state.cloud.projectId ?? state.cloud.currentProjectId ?? null,
        userId: state.auth.user?.uid ?? null,
    };
}

function cancelPendingSignedOutCleanup(): SignedOutPreservation | null {
    const pending = pendingSignedOutCleanup;
    if (!pending) {
        return lastSignedOutPreservation;
    }

    clearTimeout(pending.timer);
    pendingSignedOutCleanup = null;
    return {
        projectId: pending.projectId,
        userId: pending.userId,
    };
}

function clearSignedOutAuthState(
    actions: Pick<AuthSessionActions, 'clearAuthUser' | 'clearCloudAuthority'>,
    state = useAppStore.getState(),
): void {
    if (!handleSignedOutSessionState({
        preserveSeededLocalAuth: PRESERVE_SEEDED_LOCAL_AUTH,
        state,
        storage: sessionStorage,
    })) {
        return;
    }

    actions.clearAuthUser();
    actions.clearCloudAuthority();
}

function scheduleSignedOutCleanup(
    actions: Pick<AuthSessionActions, 'clearAuthUser' | 'clearCloudAuthority'>,
    state = useAppStore.getState(),
): void {
    const preservation = getSignedOutPreservation(state);
    if (!preservation) {
        lastSignedOutPreservation = null;
        clearSignedOutAuthState(actions, state);
        return;
    }

    cancelPendingSignedOutCleanup();
    lastSignedOutPreservation = preservation;
    pendingSignedOutCleanup = {
        ...preservation,
        timer: setTimeout(() => {
            const pending = pendingSignedOutCleanup;
            if (!pending) return;
            pendingSignedOutCleanup = null;
            clearSignedOutAuthState(actions);
        }, SIGNED_OUT_AUTH_CLEAR_DELAY_MS),
    };
}

function handleSignedInAuthProfile(userProfile: UserProfile, actions: AuthSessionActions): void {
    const signedOutPreservation = cancelPendingSignedOutCleanup();
    lastSignedOutPreservation = null;
    if (
        signedOutPreservation?.projectId
        && signedOutPreservation.userId !== userProfile.uid
    ) {
        actions.clearCloudSession();
    }
    actions.setAuthUser(userProfile);
}

function handleSignedOutAuthProfile(actions: AuthSessionActions): void {
    const currentState = useAppStore.getState();
    if (
        shouldIgnoreSignedOutSessionForSeededLocalAuth(
            currentState,
            PRESERVE_SEEDED_LOCAL_AUTH,
        )
    ) {
        actions.setUser(currentState.auth.user);
        return;
    }

    scheduleSignedOutCleanup(actions, currentState);
}

function handleAuthProfileChange(
    userProfile: UserProfile | null,
    actions: AuthSessionActions,
): void {
    actions.setHasResolvedAuth(true);

    if (userProfile) {
        handleSignedInAuthProfile(userProfile, actions);
        return;
    }

    handleSignedOutAuthProfile(actions);
}

export function useLayoutAuthSession() {
    const seededLocalAuthUser = useAppStore(state => (
        PRESERVE_SEEDED_LOCAL_AUTH && state.auth.isAuthenticated
            ? state.auth.user
            : null
    ));
    const setStoreUser = useAppStore(state => state.metaActions.setUser);
    const setCloudState = useAppStore(state => state.metaActions.setCloudState);
    const clearCloudSession = useAppStore(state => state.metaActions.clearCloudSession);
    const [user, setUser] = useState<UserProfile | null>(null);
    const [hasResolvedAuth, setHasResolvedAuth] = useState(false);

    const setAuthUser = useCallback((nextUser: UserProfile | null) => {
        setStoreUser(nextUser);
        notifyActiveAuthUserSetters(nextUser);
    }, [setStoreUser]);

    const clearAuthUser = useCallback(() => {
        setAuthUser(null);
    }, [setAuthUser]);

    const clearCloudAuthority = useCallback(() => {
        setCloudState({
            hasLoadedCloudProject: false,
            hasLoadedPersonnel: false,
            member: null,
            role: null,
        });
    }, [setCloudState]);

    useEffect(() => {
        activeAuthUserSetters.add(setUser);
        return () => {
            activeAuthUserSetters.delete(setUser);
        };
    }, []);

    useEffect(() => {
        const actions = {
            clearAuthUser,
            clearCloudAuthority,
            clearCloudSession,
            setAuthUser,
            setHasResolvedAuth,
            setUser,
        };
        const unsubscribe = initAuth(userProfile => handleAuthProfileChange(userProfile, actions));

        return unsubscribe;
    }, [clearAuthUser, clearCloudAuthority, clearCloudSession, setAuthUser]);

    const effectiveSeededLocalAuthUser = PRESERVE_SEEDED_LOCAL_AUTH ? seededLocalAuthUser : null;

    return {
        clearAuthUser,
        hasResolvedAuth: hasResolvedAuth || Boolean(effectiveSeededLocalAuthUser),
        user: effectiveSeededLocalAuthUser ?? user,
    };
}
